<template>
  <div>
    <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
      <h4 class="d-flex align-items-center">
        <font-awesome-icon icon="fas fa-laptop-house" />
        <span class="ml-1">優惠券資料</span>
      </h4>
      <b-overlay
        :show="loading"
        spinner-variant="primary"
        spinner-type="grow"
        spinner-small
        rounded="sm"
      >
        <b-form class="mt-3" @submit.prevent="handleSubmit(onSubmit)" key="2">
          <div class="mt-2 mt-sm-3 position-relative">
            <b-row>
              <!-- Coupon id -->
              <b-col cols="12" md="6">
                <validation-provider
                  #default="validationContext"
                  name="此欄"
                  rules="required"
                  vid="v_code"
                >
                  <b-form-group
                    class="required"
                    label="優惠券號碼"
                    label-for="code"
                  >
                    <b-form-input
                      id="code"
                      :state="getValidationState(validationContext)"
                      v-model="newCouponData.code"
                      @update="checkCode"
                      placeholder="客戶輸入以獲得折扣的代碼"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- title -->
              <b-col cols="12" md="6">
                <validation-provider
                  #default="validationContext"
                  name="此欄"
                  rules="required"
                  vid="v_title"
                >
                  <b-form-group
                    class="required"
                    label="優惠券名稱"
                    label-for="title"
                  >
                    <b-form-input
                      id="title"
                      :state="getValidationState(validationContext)"
                      v-model="newCouponData.title"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12">
                <hr />
              </b-col>

              <!-- description -->
              <b-col cols="12" md="6">
                <b-form-group class="" label="描述" label-for="description">
                  <b-form-input
                    id="description"
                    v-model="newCouponData.description"
                  />
                </b-form-group>
              </b-col>

              <!-- customers -->
              <b-col cols="12" md="6">
                <b-form-group
                  label="客戶："
                  label-for="customers"
                  class="multi-select"
                >
                  <v-select
                    id="customers"
                    v-model="newCouponData.customers"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="customerOptions"
                    :clearable="true"
                    label="name"
                    multiple
                    :reduce="(val) => val.id"
                    placeholder="選擇客戶"
                    :filter="customerSearch"
                    v-b-popover.hover.top="
                      '限制可以使用優惠券的使用者。 留空表示無限制'
                    "
                  >
                    <template #option="{ name_cn, user_number }">
                      [{{ user_number }}] {{ name_cn }}
                    </template>
                    <template #selected-option="{ name_cn, user_number }">
                      [{{ user_number }}] {{ name_cn }}
                    </template>
                  </v-select>
                </b-form-group>
              </b-col>

              <b-col cols="12">
                <hr />
              </b-col>

              <!-- amount -->
              <b-col cols="12" md="6">
                <validation-provider
                  #default="validationContext"
                  name="此欄"
                  :rules="`${!newCouponData.percentage ? 'required' : ''}`"
                  vid="v_amount"
                >
                  <b-form-group
                    :class="`${!newCouponData.percentage ? 'required' : ''}`"
                    label="折扣金額："
                    label-for="amount"
                  >
                    <b-form-input
                      id="amount"
                      type="number"
                      v-model="newCouponData.amount"
                      @input="onAmountInput"
                      placeholder="折扣金額"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12" md="6">
                <validation-provider
                  #default="validationContext"
                  name="此欄"
                  :rules="`${!newCouponData.amount ? 'required' : ''}`"
                  vid="v_percentage"
                >
                  <b-form-group
                    :class="`${!newCouponData.amount ? 'required' : ''}`"
                    label="折扣百分比"
                    label-for="percentage"
                  >
                    <b-input-group append="%">
                      <b-form-input
                        id="percentage"
                        type="number"
                        v-model="newCouponData.percentage"
                        @input="onPercentageInput"
                        placeholder="折扣百分比"
                        v-limit-number:max="100"
                      />
                    </b-input-group>
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12">
                <hr />
              </b-col>

              <!-- date_from -->
              <b-col cols="12" md="6">
                <validation-provider
                  #default="validationContext"
                  name="此欄"
                  rules="required"
                  vid="v_date_from"
                >
                  <b-form-group class="required" label="從" label-for="from">
                    <flat-pickr
                      v-model="newCouponData.date_from"
                      class="form-control"
                      :config="{
                        allowInput: true,
                        altInput: true,
                        altFormat: 'Y-m-d',
                        dateFormat: 'Y-m-d',
                      }"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- date_to -->
              <b-col cols="12" md="6">
                <validation-provider
                  #default="validationContext"
                  name="此欄"
                  rules="required"
                  vid="v_date_to"
                >
                  <b-form-group class="required" label="到" label-for="to">
                    <flat-pickr
                      v-model="newCouponData.date_to"
                      class="form-control"
                      :config="{
                        allowInput: true,
                        altInput: true,
                        altFormat: 'Y-m-d',
                        dateFormat: 'Y-m-d',
                      }"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col cols="12">
                <hr />
              </b-col>

              <!-- total_amount-->
              <b-col cols="12" md="6">
                <b-form-group
                  label="總金額："
                  label-for="total_amount"
                  v-b-popover.hover.top="
                    '優惠券生效前必須達到的總金額。 留空表示無限制'
                  "
                >
                  <b-form-input
                    id="total_amount"
                    type="number"
                    v-model="newCouponData.total_amount"
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12" md="6">
                <b-form-group
                  label="每個客戶的使用次數"
                  label-for="uses_per_customer"
                >
                  <b-form-input
                    id="uses_per_customer"
                    type="number"
                    v-model="newCouponData.uses_per_customer"
                    v-b-popover.hover.top="
                      '單一客戶可以使用優惠券的最大次數。 留空表示無限制'
                    "
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12">
                <hr />
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group
                  label="每張優惠券使用"
                  label-for="uses_per_coupon"
                >
                  <b-form-input
                    id="uses_per_coupon"
                    type="number"
                    v-model="newCouponData.uses_per_coupon"
                    v-b-popover.hover.top="
                      '任何客戶可以使用優惠券的最大次數。 留空表示無限制'
                    "
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12" md="6">
                <b-form-group
                  label="狀態"
                  label-for="is_active"
                  label-cols-md="2"
                  class="mt-2"
                >
                  <!-- <b-form-input id="is_active"  v-model="newCouponData.is_active" /> -->
                  <b-form-checkbox
                    :checked="newCouponData.is_active"
                    name="is_active"
                    switch
                    inline
                    v-model="newCouponData.is_active"
                    value="1"
                    style="margin-top: 5px"
                  >
                    {{ newCouponData.is_active ? "啟用" : "禁止" }}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
            <div
              class="d-flex mt-1 flex-wrap flex-lg-nowrap justify-content-end"
            >
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-sm-2 phone-w100"
                type="submit"
              >
                <feather-icon size="16" class="mr-1" icon="CheckCircleIcon" />
                提交
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                @click="hide"
                class="mt-1 mt-sm-0 phone-w100"
              >
                取消
              </b-button>
            </div>
          </div>
        </b-form>
      </b-overlay>
    </validation-observer>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BForm,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormCheckbox,
  BFormRadioGroup,
  BFormRadio,
  BButton,
  BOverlay,
  BInputGroup,
  VBPopover,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import { ref, watch } from "@vue/composition-api";
import vSelect from "vue-select";
import store from "@/store";
import Ripple from "vue-ripple-directive";
import "animate.css";
import { ValidationProvider, ValidationObserver, localize } from "vee-validate";
import zh_CN from "vee-validate/dist/locale/zh_CN.json";
localize("zh_cn", zh_CN);
import formValidation from "@core/comp-functions/forms/form-validation";
import { required, email } from "@validations";

export default {
  data() {
    return {
      required,
    };
  },
  directives: {
    Ripple,
    "b-popover": VBPopover,
    "limit-number": {
      bind: function (el, binding) {
        const max = Number(binding.value) || Infinity;
        el.addEventListener("input", function (event) {
          const input = event.target;
          let value = Number(input.value);
          if (isNaN(value) || value < 0) {
            value = 0;
          } else if (value > max) {
            value = max;
          }
          if (input.value !== value.toString()) {
            input.value = value.toString();
            // Dispatch a change event instead of input
            input.dispatchEvent(new Event("change"));
          }
        });
      },
    },
  },
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormInvalidFeedback,
    BFormCheckbox,
    BFormRadioGroup,
    BFormRadio,
    BRow,
    BCol,
    BButton,
    BOverlay,
    BInputGroup,
    flatPickr,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    VBPopover,
  },
  methods: {
    hide() {
      this.$router.replace("/coupon/list");
    },

    onAmountInput(value) {
      if (value && value > 0) {
        this.newCouponData.percentage = null;
      }
    },
    onPercentageInput(value) {
      if (value && value > 0) {
        this.newCouponData.amount = null;
      }
    },
    checkCode() {
      this.newCouponData.code = this.newCouponData.code
        .toUpperCase()
        .replace(/ /g, "-")
        .replace(/[^\w-]+/g, "");
    },
    onSubmit() {
      if (typeof this.newCouponData.customers === "object") {
        this.newCouponData.customers = this.newCouponData.customers.map(
          (item) => {
            return item.id || item;
          }
        );
      }
      this.$swal({
        title: "你確定嗎?",
        showCancelButton: true,
        confirmButtonText: "提交",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.loading = true;
          this.newCouponData.is_active = this.newCouponData.is_active ? 1 : 0;
          store
            .dispatch("coupon/editCoupon", this.newCouponData)
            .then((response) => {
              this.loading = false;
              this.$swal({
                text: response.data.message,
                icon: "success",
                confirmButtonText: "確定",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
              this.hide();
              this.$emit("refetech-data");
            })
            .catch((error) => {
              this.loading = false;
              this.$swal({
                text: error.response.data.message,
                icon: "error",
                confirmButtonText: "確定",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
            });
        }
      });
    },
    customerSearch(options, search) {
      var returnOptions = options.filter((item) => {
        return (
          item.name.toLowerCase().includes(search.toLowerCase()) ||
          item.customer_number.toLowerCase().includes(search.toLowerCase())
        );
      });
      return returnOptions;
    },
    makeToast(variant = null, title = null, content = null) {
      this.$bvToast.toast(content, {
        title: title,
        variant,
        solid: true,
      });
    },
  },
  props: {
    couponData: {
      type: Object,
      required: true,
    },
    customerOptions: {
      type: Array,
      required: true,
    },
  },

  setup(props, { emit }) {
    const newCouponData = ref({ id: 0, is_active: 1 });

    watch(
      () => props.couponData,
      (newVal) => {
        newCouponData.value = { ...newVal };
      }
    );

    const resetCouponsData = () => {
      newCouponData.value = JSON.parse(
        JSON.stringify({
          id: 0,
          is_active: 1,
        })
      );
    };

    const loading = ref(false);

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetCouponsData);

    return {
      loading,
      refFormObserver,
      getValidationState,
      resetForm,
      newCouponData,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style lang="scss" scoped>
</style>
